export const ACCENT_THEME_STORAGE_KEY = "ln-accent-theme-v1";

const CUSTOM_PROPS = [
    "--ln-accent-rgb",
    "--ln-accent",
    "--ln-accent-hover",
    "--ln-accent-soft",
    "--ln-accent-muted",
    "--ln-accent-light",
    "--ln-accent-strong",
    "--primary-color",
];

export const ACCENT_PRESETS = [
    {
        id: "ocean",
        label: "Ocean",
        hex: "#0871a1",
        note: "Default — refined from deep teal",
    },
    {
        id: "depth",
        label: "Depth",
        hex: "#02537d",
        note: "Your original deep blue",
    },
    {
        id: "sky",
        label: "Sky",
        hex: "#0284c7",
        note: "Bright sky blue",
    },
    {
        id: "teal",
        label: "Teal",
        hex: "#0d9488",
        note: "Green-teal",
    },
    {
        id: "indigo",
        label: "Indigo",
        hex: "#4f46e5",
        note: "Violet B2B",
    },
];

export function normalizeHex(hex) {
    if (typeof hex !== "string") {
        return null;
    }
    let h = hex.trim();
    if (!h.startsWith("#")) {
        h = `#${h}`;
    }
    if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(h)) {
        return null;
    }
    if (h.length === 4) {
        const r = h[1];
        const g = h[2];
        const b = h[3];
        h = `#${r}${r}${g}${g}${b}${b}`;
    }
    return h.toLowerCase();
}

export function hexToRgb(hex) {
    const n = normalizeHex(hex);
    if (!n) {
        return null;
    }
    const num = parseInt(n.slice(1), 16);
    return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255,
    };
}

function rgbToHex(r, g, b) {
    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
    return `#${[clamp(r), clamp(g), clamp(b)]
        .map((x) => x.toString(16).padStart(2, "0"))
        .join("")}`;
}

function mixRgb(a, b, t) {
    return {
        r: a.r + (b.r - a.r) * t,
        g: a.g + (b.g - a.g) * t,
        b: a.b + (b.b - a.b) * t,
    };
}

function darkenRgb(rgb, amount) {
    const t = Math.max(0, Math.min(1, amount));
    return mixRgb(rgb, { r: 0, g: 0, b: 0 }, t);
}

function lightenRgb(rgb, amount) {
    const t = Math.max(0, Math.min(1, amount));
    return mixRgb(rgb, { r: 255, g: 255, b: 255 }, t);
}

export function applyAccentFromHex(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) {
        return false;
    }
    const root = document.documentElement.style;
    const hover = darkenRgb(rgb, 0.14);
    const muted = darkenRgb(rgb, 0.32);
    const light = lightenRgb(rgb, 0.38);
    const strong = lightenRgb(rgb, 0.06);

    root.setProperty("--ln-accent-rgb", `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    root.setProperty("--ln-accent", normalizeHex(hex));
    root.setProperty("--ln-accent-hover", rgbToHex(hover.r, hover.g, hover.b));
    root.setProperty(
        "--ln-accent-soft",
        `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`
    );
    root.setProperty("--ln-accent-muted", rgbToHex(muted.r, muted.g, muted.b));
    root.setProperty("--ln-accent-light", rgbToHex(light.r, light.g, light.b));
    root.setProperty(
        "--ln-accent-strong",
        rgbToHex(strong.r, strong.g, strong.b)
    );
    root.setProperty("--primary-color", normalizeHex(hex));
    return true;
}

export function clearAccentOverrides() {
    try {
        localStorage.removeItem(ACCENT_THEME_STORAGE_KEY);
    } catch {
        /* ignore */
    }
    CUSTOM_PROPS.forEach((p) =>
        document.documentElement.style.removeProperty(p)
    );
}

export function saveAccentPreset(presetId) {
    try {
        localStorage.setItem(
            ACCENT_THEME_STORAGE_KEY,
            JSON.stringify({ kind: "preset", id: presetId })
        );
    } catch {
        /* ignore */
    }
}

export function saveAccentCustom(hex) {
    const n = normalizeHex(hex);
    if (!n) {
        return false;
    }
    try {
        localStorage.setItem(
            ACCENT_THEME_STORAGE_KEY,
            JSON.stringify({ kind: "custom", hex: n })
        );
    } catch {
        /* ignore */
    }
    return true;
}

export function loadAndApplyStoredAccent() {
    let raw;
    try {
        raw = localStorage.getItem(ACCENT_THEME_STORAGE_KEY);
    } catch {
        return;
    }
    if (!raw) {
        return;
    }
    let data;
    try {
        data = JSON.parse(raw);
    } catch {
        localStorage.removeItem(ACCENT_THEME_STORAGE_KEY);
        return;
    }
    if (!data || typeof data !== "object") {
        return;
    }
    if (data.kind === "custom" && data.hex) {
        applyAccentFromHex(data.hex);
        return;
    }
    if (data.kind === "preset" && data.id) {
        const preset = ACCENT_PRESETS.find((p) => p.id === data.id);
        if (preset) {
            applyAccentFromHex(preset.hex);
        }
    }
}
